// Phase 2B — Sales CRM main page: metrics, filters, Pipeline/List views, new lead form.
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Bell, LayoutGrid, List as ListIcon, Search, X, Sparkles } from "lucide-react";
import PipelineView from "./PipelineView";
import ListView from "./ListView";
import LeadDetail from "./LeadDetail";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_LABELS = {
    new_lead: "New Lead", qualified: "Qualified", contacted: "Contacted", replied: "Replied",
    discovery: "Discovery", proposal_sent: "Proposal Sent", negotiation: "Negotiation", won: "Won",
    nurture: "Nurture", lost: "Lost", spam: "Spam",
};
const SOURCES = [["website_inquiry", "Website Inquiry"], ["instagram", "Instagram"], ["whatsapp", "WhatsApp"],
    ["email", "Email"], ["google_maps", "Google Maps"], ["chatgpt_discovery", "ChatGPT Discovery"],
    ["referral", "Referral"], ["walk_in", "Walk-in / In-person"], ["existing_client", "Existing Client"],
    ["manual", "Manual Entry"], ["other", "Other"]];

export { STAGE_LABELS, SOURCES, API };

function MetricTile({ label, value, hint, testid }) {
    return (
        <div data-testid={testid} title={hint}
            className="bg-white rounded-xl border border-neutral-200 px-4 py-3 cursor-help">
            <div className="font-serif text-2xl text-cafe-espresso">{value}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mt-0.5">{label}</div>
        </div>
    );
}

function NewLeadModal({ onClose, onCreated }) {
    const [f, setF] = useState({ brand_name: "", business_type: "", location: "", lead_source: "manual",
        contact_channel: "", contact_name: "", instagram_url: "", website_url: "", maps_url: "",
        email: "", phone: "", record_class: "live", stage: "new_lead", next_action: "Send first outreach",
        followup_date: new Date().toISOString().slice(0, 10), budget_signal: "", launch_date: "",
        campaign_need: "", notes: "", est_value: "" });
    const [dupes, setDupes] = useState(null);
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

    const submit = async (force = false) => {
        if (!f.brand_name.trim()) return toast.error("Brand name is required");
        setBusy(true);
        try {
            const payload = { ...f, est_value: f.est_value ? parseFloat(f.est_value) : null, force };
            const { data } = await axios.post(`${API}/crm/leads`, payload);
            toast.success("Lead created");
            onCreated(data.lead.id);
        } catch (err) {
            const d = err?.response?.data?.detail;
            if (err?.response?.status === 409 && d?.matches) { setDupes(d.matches); }
            else toast.error(typeof d === "string" ? d : "Could not create lead");
        } finally { setBusy(false); }
    };

    const F = ({ label, k, type = "text", req }) => (
        <label className="block text-xs text-neutral-600">
            {label}{req && <span className="text-red-600"> *</span>}
            <input data-testid={`lead-form-${k}`} type={type} value={f[k]} onChange={set(k)}
                className="mt-1 w-full border border-neutral-300 rounded-lg px-2.5 py-1.5 text-sm bg-white" />
        </label>
    );

    return (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center overflow-y-auto p-4" data-testid="new-lead-modal">
            <div className="bg-[#FAF6EF] rounded-2xl w-full max-w-2xl p-6 my-8">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="font-serif text-xl text-cafe-espresso">New Lead</h3>
                    <button data-testid="new-lead-close" onClick={onClose}><X size={18} /></button>
                </div>
                {dupes && (
                    <div className="mb-4 border border-amber-400 bg-amber-50 rounded-xl p-3" data-testid="duplicate-warning">
                        <div className="text-sm font-medium text-amber-800">Possible duplicate found — review before creating:</div>
                        {dupes.map((m) => (
                            <div key={m.id} className="flex justify-between items-center text-sm mt-2">
                                <span>{m.brand} · {STAGE_LABELS[m.stage] || m.stage} · {m.contact?.email || m.contact?.phone || ""}</span>
                                <button data-testid={`open-existing-${m.id}`} className="underline text-cafe-olive"
                                    onClick={() => { onCreated(m.id); }}>Open existing</button>
                            </div>
                        ))}
                        <button data-testid="create-anyway-btn" disabled={busy} onClick={() => submit(true)}
                            className="mt-3 text-xs px-3 py-1.5 rounded-full border border-amber-600 text-amber-800">
                            Not a duplicate — create anyway
                        </button>
                    </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <F label="Brand name" k="brand_name" req />
                    <F label="Business type" k="business_type" />
                    <F label="Location" k="location" />
                    <label className="block text-xs text-neutral-600">Lead source
                        <select data-testid="lead-form-source" value={f.lead_source} onChange={set("lead_source")}
                            className="mt-1 w-full border border-neutral-300 rounded-lg px-2 py-1.5 text-sm bg-white">
                            {SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                    </label>
                    <F label="Contact channel" k="contact_channel" />
                    <F label="Contact name" k="contact_name" />
                    <F label="Instagram URL" k="instagram_url" />
                    <F label="Website URL" k="website_url" />
                    <F label="Google Maps URL" k="maps_url" />
                    <F label="Email" k="email" />
                    <F label="Phone / WhatsApp" k="phone" />
                    <label className="block text-xs text-neutral-600">Classification
                        <select data-testid="lead-form-class" value={f.record_class} onChange={set("record_class")}
                            className="mt-1 w-full border border-neutral-300 rounded-lg px-2 py-1.5 text-sm bg-white">
                            <option value="live">LIVE CUSTOMER</option><option value="demo">DEMO</option>
                            <option value="test">TEST / QA</option><option value="internal">INTERNAL</option>
                        </select>
                    </label>
                    <label className="block text-xs text-neutral-600">Stage
                        <select data-testid="lead-form-stage" value={f.stage} onChange={set("stage")}
                            className="mt-1 w-full border border-neutral-300 rounded-lg px-2 py-1.5 text-sm bg-white">
                            {Object.entries(STAGE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                    </label>
                    <F label="Next action" k="next_action" req />
                    <F label="Follow-up date" k="followup_date" type="date" req />
                    <F label="Estimated value (RM)" k="est_value" type="number" />
                    <F label="Budget signal" k="budget_signal" />
                    <F label="Launch date" k="launch_date" />
                    <F label="Campaign need" k="campaign_need" />
                </div>
                <label className="block text-xs text-neutral-600 mt-3">Notes
                    <textarea data-testid="lead-form-notes" value={f.notes} onChange={set("notes")} rows={2}
                        className="mt-1 w-full border border-neutral-300 rounded-lg px-2.5 py-1.5 text-sm bg-white" />
                </label>
                <div className="flex justify-end gap-2 mt-4">
                    <button data-testid="new-lead-cancel" onClick={onClose} className="px-4 py-2 text-sm rounded-full border border-neutral-300">Cancel</button>
                    <button data-testid="new-lead-submit" disabled={busy} onClick={() => submit(false)}
                        className="px-5 py-2 text-sm rounded-full bg-cafe-espresso text-white">
                        {busy ? "Creating…" : "Create lead"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function SalesCRM({ onOpenBrain }) {
    const [view, setView] = useState("pipeline");
    const [cls, setCls] = useState("live");
    const [metrics, setMetrics] = useState(null);
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [filters, setFilters] = useState({ stage: "", priority: "", source: "", followup: "" });
    const [showNew, setShowNew] = useState(false);
    const [openLead, setOpenLead] = useState(() => new URLSearchParams(window.location.search).get("lead") || null);
    const [notif, setNotif] = useState(null);
    const [showNotif, setShowNotif] = useState(false);

    const load = useCallback(async () => {
        try {
            const params = new URLSearchParams({ cls, q, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) });
            const [m, l, n] = await Promise.all([
                axios.get(`${API}/crm/metrics?cls=${cls}`),
                axios.get(`${API}/crm/leads?${params}`),
                axios.get(`${API}/crm/notifications`),
            ]);
            setMetrics(m.data); setData(l.data); setNotif(n.data);
        } catch { toast.error("Could not load CRM"); }
    }, [cls, q, filters]);

    useEffect(() => { load(); }, [load]);

    const defs = metrics?.definitions || {};
    const conv = metrics?.conversion_rate;

    return (
        <div data-testid="sales-crm" className="space-y-5">
            <details className="text-xs text-neutral-600" data-testid="crm-guide">
                <summary className="cursor-pointer select-none text-[11px] text-cafe-olive">? Sales Pipeline 怎么用 · How to use</summary>
                <div className="mt-2 space-y-1 bg-white rounded-xl border border-neutral-200 p-3">
                    <div><b>流程</b>：New Lead（新询问）→ Qualified（值得跟进）→ Contacted（已联系）→ Replied（对方回复）→ Discovery（了解需求）→ Proposal Sent（已报价）→ Negotiation（谈价）→ <b>Won（成交）</b>。</div>
                    <div>每张卡片下方的 "Move to stage…" 下拉可移动阶段；点卡片本身打开详情，设置下一步动作和跟进日期（Set next action）。</div>
                    <div>Lead 移到 <b>Won</b> 之后，会提示你转成 Client Project（正式项目）——之后的拍摄/交付/收款都在 Client Projects 里管理。</div>
                    <div><b>AI Sales Brain</b>：每张卡片的 "Analyse with AI Sales Brain" 会打开该客户的 AI 工作区 — 粘贴商家截图、写下你的 Direction，AI 只根据证据分析（缺资料会标 Not confirmed）。</div>
                </div>
            </details>
            <div className="flex flex-wrap items-center gap-2 justify-between">
                <div className="flex items-center gap-2">
                    <button data-testid="crm-new-lead-btn" onClick={() => setShowNew(true)}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-cafe-espresso text-white text-sm">
                        <Plus size={15} /> New Lead
                    </button>
                    <button data-testid="crm-open-brain-btn" onClick={() => onOpenBrain ? onOpenBrain() : (data?.cards?.[0] && setOpenLead(`${data.cards[0].id}#brain`))}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-cafe-espresso text-cafe-espresso text-sm bg-white">
                        <Sparkles size={15} /> Open AI Sales Brain
                    </button>
                    <div className="flex rounded-full border border-neutral-300 overflow-hidden">
                        <button data-testid="crm-view-pipeline" onClick={() => setView("pipeline")}
                            className={`px-3 py-1.5 text-xs flex items-center gap-1 ${view === "pipeline" ? "bg-cafe-espresso text-white" : "bg-white"}`}>
                            <LayoutGrid size={13} /> Pipeline
                        </button>
                        <button data-testid="crm-view-list" onClick={() => setView("list")}
                            className={`px-3 py-1.5 text-xs flex items-center gap-1 ${view === "list" ? "bg-cafe-espresso text-white" : "bg-white"}`}>
                            <ListIcon size={13} /> List
                        </button>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <button data-testid="crm-notif-bell" onClick={() => setShowNotif(!showNotif)}
                            className="relative p-2 rounded-full border border-neutral-300 bg-white">
                            <Bell size={15} />
                            {notif?.unread > 0 && <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[9px] rounded-full px-1.5">{notif.unread}</span>}
                        </button>
                        {showNotif && (
                            <div data-testid="crm-notif-panel" className="absolute right-0 mt-2 w-80 bg-white border border-neutral-200 rounded-xl shadow-lg z-40 p-3">
                                <NotifPanel notif={notif} onRead={async () => { await axios.post(`${API}/crm/notifications/read`); load(); }} />
                            </div>
                        )}
                    </div>
                    <select data-testid="crm-class-filter" value={cls} onChange={(e) => setCls(e.target.value)}
                        className="border border-neutral-300 rounded-full px-3 py-1.5 text-xs bg-white">
                        <option value="live">Live customers only</option>
                        <option value="all">Include demo/test</option>
                        <option value="internal">Internal</option>
                        <option value="unclassified">Unclassified</option>
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <MetricTile testid="metric-new-week" label="New leads this week" value={metrics?.new_leads_week ?? "—"} hint={defs.new_leads_week} />
                <MetricTile testid="metric-followups" label="Follow-ups due" value={metrics?.followups_due ?? "—"} hint={defs.followups_due} />
                <MetricTile testid="metric-proposal-value" label="Active proposal value" value={`RM ${Math.round(metrics?.active_proposal_value || 0).toLocaleString()}`} hint={defs.active_proposal_value} />
                <MetricTile testid="metric-won-month" label="Won this month" value={`RM ${Math.round(metrics?.won_this_month || 0).toLocaleString()}`} hint={defs.won_this_month} />
                <MetricTile testid="metric-conversion" label="Conversion rate" value={conv == null ? "No closed leads yet" : `${conv}%`} hint={defs.conversion_rate} />
            </div>

            <div className="flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[180px] max-w-xs">
                    <Search size={13} className="absolute left-2.5 top-2.5 text-neutral-400" />
                    <input data-testid="crm-search" value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Search brand, contact, email, phone, IG, location…"
                        className="w-full pl-8 pr-3 py-1.5 text-sm rounded-full border border-neutral-300 bg-white" />
                </div>
                {[["stage", ["", ...Object.keys(STAGE_LABELS)], (v) => STAGE_LABELS[v] || "All stages"],
                  ["priority", ["", "hot", "warm", "nurture", "low"], (v) => v ? v[0].toUpperCase() + v.slice(1) : "All priorities"],
                  ["followup", ["", "overdue", "due_today", "upcoming", "missing"], (v) => v ? v.replace("_", " ") : "All follow-ups"],
                  ["source", ["", ...SOURCES.map(([v]) => v)], (v) => (SOURCES.find(([s]) => s === v) || [])[1] || "All sources"]]
                    .map(([k, opts, lbl]) => (
                    <select key={k} data-testid={`crm-filter-${k}`} value={filters[k]}
                        onChange={(e) => setFilters({ ...filters, [k]: e.target.value })}
                        className="border border-neutral-300 rounded-full px-2.5 py-1.5 text-xs bg-white">
                        {opts.map((o) => <option key={o} value={o}>{lbl(o)}</option>)}
                    </select>
                ))}
            </div>

            {!data ? <div className="text-sm text-neutral-500 py-10 text-center">Loading…</div>
                : view === "pipeline"
                    ? <PipelineView data={data} onOpen={setOpenLead} onChanged={load} />
                    : <ListView data={data} onOpen={setOpenLead} />}

            {showNew && <NewLeadModal onClose={() => setShowNew(false)}
                onCreated={(id) => { setShowNew(false); setOpenLead(id); load(); }} />}
            {openLead && <LeadDetail leadId={openLead} onClose={() => { setOpenLead(null); load(); }} />}
        </div>
    );
}

function NotifPanel({ notif, onRead }) {
    const [settings, setSettings] = useState(null);
    useEffect(() => { axios.get(`${API}/crm/notifications/settings`).then((r) => setSettings(r.data)).catch(() => {}); }, []);
    const save = async (s) => { setSettings(s); await axios.put(`${API}/crm/notifications/settings`, s); toast.success("Saved"); };
    return (
        <div className="space-y-2 text-sm">
            <div className="flex justify-between items-center">
                <span className="font-medium">Notifications</span>
                <button data-testid="notif-mark-read" className="text-xs underline" onClick={onRead}>Mark all read</button>
            </div>
            {(notif?.notifications || []).slice(0, 6).map((n, i) => (
                <div key={i} className={`text-xs p-2 rounded-lg ${n.read ? "bg-neutral-50" : "bg-amber-50"}`}>
                    <div className="font-medium">{n.title}</div>
                    <div className="text-neutral-500">{n.services || ""} · {(n.at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
            ))}
            {!(notif?.notifications || []).length && <div className="text-xs text-neutral-500">No notifications yet.</div>}
            {(notif?.recent_failures || []).length > 0 && (
                <div className="text-xs text-red-700 bg-red-50 rounded-lg p-2" data-testid="notif-failures">
                    {notif.recent_failures.length} delivery failure(s) — leads were preserved.
                </div>
            )}
            {settings && (
                <div className="border-t pt-2 space-y-1.5">
                    <label className="flex items-center gap-2 text-xs">
                        <input data-testid="notif-inapp-toggle" type="checkbox" checked={settings.inapp} onChange={(e) => save({ ...settings, inapp: e.target.checked })} />
                        In-app notification on new inquiry
                    </label>
                    <label className="flex items-center gap-2 text-xs">
                        <input data-testid="notif-email-toggle" type="checkbox" checked={settings.email} onChange={(e) => save({ ...settings, email: e.target.checked })} />
                        Email me on new inquiry
                    </label>
                    {settings.email && (
                        <input data-testid="notif-email-to" value={settings.email_to} placeholder="your@email.com"
                            onChange={(e) => setSettings({ ...settings, email_to: e.target.value })}
                            onBlur={() => save(settings)}
                            className="w-full border border-neutral-300 rounded-lg px-2 py-1 text-xs" />
                    )}
                </div>
            )}
        </div>
    );
}
